import { memo, useCallback, useEffect, useRef, useState } from "react";

import { getAllWorkflowRoles, updateRoleWorkflows } from "@/api/workflow-role";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HashLoader } from "react-spinners";
import { useDebouncedCallback } from "use-debounce";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

interface Props {
  roleId: number;
  isOpen: boolean;
  onClose: () => void;
}

const PAGE_SIZE = 10;

const WorkflowListModal: React.FC<Props> = ({ isOpen, onClose, roleId }) => {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>انتخاب گردش کار</DialogTitle>
        </DialogHeader>

        <Content roleId={roleId} onClose={onClose} />
      </DialogContent>
    </Dialog>
  );
};

const Content = memo(
  ({ roleId, onClose }: { roleId: number; onClose: () => void }) => {
    const [search, setSearch] = useState("");
    const [selectedWorkflows, setSelectedWorkflows] = useState<number[]>([]);

    const [page, setPage] = useState(1);
    const [allData, setAllData] = useState<any[]>([]);

    const queryClient = useQueryClient();

    // 🔹 FETCH DATA
    const { data, isFetching } = useQuery({
      queryKey: ["role-workflows", roleId, page, search],
      queryFn: () =>
        getAllWorkflowRoles(roleId, {
          page,
          size: PAGE_SIZE,
          search
        })
    });

    // 🔹 append data (infinite scroll)
    useEffect(() => {
      if (!data?.data) return;

      if (page === 1) {
        setAllData(data.data);
      } else {
        setAllData((prev) => [...prev, ...data.data]);
      }
    }, [data]);

    // 🔹 reset on search change
    const handleSearch = useDebouncedCallback((value: string) => {
      setPage(1);
      setAllData([]);
      setSearch(value);
    }, 800);

    // 🔹 init selected
    useEffect(() => {
      setSelectedWorkflows(allData.filter((x) => x.isAccess).map((x) => x.id));
    }, [allData]);

    // 🔹 toggle checkbox
    const changeHandler = (id: number) => {
      setSelectedWorkflows((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      );
    };

    // 🔹 mutation
    const { mutate, isPending } = useMutation({
      mutationFn: () => updateRoleWorkflows(roleId, selectedWorkflows),

      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["role-workflows", roleId]
        });
        onClose();
      }
    });

    // 🔹 infinite scroll observer
    const observer = useRef<IntersectionObserver | null>(null);

    const lastItemRef = useCallback(
      (node: HTMLDivElement | null) => {
        if (isFetching) return;

        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) {
            if (data?.data?.length === PAGE_SIZE) {
              setPage((prev) => prev + 1);
            }
          }
        });

        if (node) observer.current.observe(node);
      },
      [isFetching, data]
    );

    return (
      <>
        {/* 🔍 Search */}
        <Input
          placeholder="جستجو"
          onChange={(e) => handleSearch(e.target.value)}
          className="w-full md:w-1/2"
        />

        {/* 📜 List */}
        <div className="mt-5 h-64 divide-y divide-slate-300 overflow-y-auto rounded-md border border-slate-300">
          {isFetching && page === 1 ? (
            <div className="flex h-full items-center justify-center">
              <HashLoader color="#0099A5" size={40} />
            </div>
          ) : (
            <>
              {allData.map((workflow, index) => {
                const isLast = index === allData.length - 1;

                return (
                  <div
                    key={workflow.id}
                    ref={isLast ? lastItemRef : null}
                    className="flex items-center gap-x-2 px-2 py-4"
                  >
                    <Checkbox
                      checked={selectedWorkflows.includes(workflow.id)}
                      onCheckedChange={() => changeHandler(workflow.id)}
                    />
                    <p className="line-clamp-1 text-sm">{workflow.name}</p>
                  </div>
                );
              })}

              {isFetching && page > 1 && (
                <div className="py-3 text-center text-sm">
                  در حال بارگذاری...
                </div>
              )}
            </>
          )}
        </div>

        {/* 💾 Save */}
        <Button
          className="mt-5 w-full"
          onClick={() => mutate()}
          disabled={isPending}
        >
          ذخیره
        </Button>
      </>
    );
  }
);

export default WorkflowListModal;
